import { RESOLUTIONS } from '$domain/events';
import type { Resolution } from '$domain/events';

/**
 * The Comeback outcomes and the rule that keeps them equal.
 *
 * Spec §7 states equal dignity as a formal constraint, not a tone note: the
 * three outcomes must not encode moral rank through button hierarchy, size,
 * colour, iconography, **order**, animation, celebration, or follow-up copy.
 *
 * Styling equality is enforced by a test. Order is enforced here, because a
 * fixed order *is* a hierarchy — whatever sits under the thumb is the one that
 * gets chosen, and whatever sits first is the one that reads as intended. One
 * default position may exist for thumb ergonomics; it must be neutral or
 * rotate. This rotates.
 */

/** The five defaults. Replaced over time by the user's own (spec §6.2). */
export const DEFAULT_TRIGGER_TAGS = [
  'bored',
  'anxious',
  'tired',
  'avoiding',
  'habit',
] as const;

export type DefaultTriggerTag = (typeof DEFAULT_TRIGGER_TAGS)[number];

/**
 * The three outcomes, rotated so no one of them owns the first position.
 *
 * Deterministic, not random: the order must be reproducible from the log (so a
 * test can assert it and so the screen does not reshuffle under a user's thumb
 * between two glances at it), and it must not depend on anything the user could
 * read as approval.
 *
 * `offset` is the number of Comebacks already resolved — a number that only
 * ever goes up and has nothing to do with which outcome was chosen.
 */
export function rotateOutcomes(offset: number): Resolution[] {
  const n = RESOLUTIONS.length;
  const start = ((offset % n) + n) % n;
  return Array.from({ length: n }, (_, i) => RESOLUTIONS[(start + i) % n] as Resolution);
}
